/**
 * OwnerIBSPanel (B4) -- the owner describes, wires and declares.
 *
 * He could read his IBS and change nothing about it: the description
 * fields sat behind the admin console and the dependency links behind
 * `require_admin`, so the person who knows what the service does and
 * what it rests on was the one person who could not write it down.
 *
 * Only rendered for someone the ESTATE says owns this IBS -- the
 * authority comes from the ownership record, not from a role, so it
 * cannot leak onto a service that is not his.
 *
 * Written for someone who is not a resilience specialist: a tolerance
 * is explained in a sentence before it is asked for.
 */
import { useToast } from "shared/toast/ToastProvider";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import { Card } from "shared/components/Card";
import { ToleranceDeclarationForm } from "twin/tolerance/ToleranceDeclarationForm";
import { useMyEstate } from "twin/home/useMyEstate";

interface CatalogueOption {
  id: string;
  code: string | null;
  name: string;
}

export function OwnerIBSPanel({ ibsId }: { ibsId: string | undefined }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const estate = useMyEstate();
  const mine = (estate.data?.ibses ?? []).find((i) => i.id === ibsId);
  const owned = mine !== undefined;
  const supporting = mine?.supporting_services ?? [];
  // Round 16 -- a Critical Internal Function serves the bank itself;
  // asking its owner who it serves and through which channel invited
  // "mobile; web; branch" on a treasury function.
  const internal = mine?.service_class === "internal";

  // One stable prefix per mounted panel, so two panels on one page
  // cannot collide on an id.
  const fieldId = useId();
  const [serviceId, setServiceId] = useState("");
  // D4 -- the fields came back EMPTY on every visit, so a description
  // saved last week read as one never written and an owner correcting a
  // typo had to retype the paragraph. The saved values seed the form
  // once the estate lands; edits in progress are not overwritten by a
  // background refetch, which is what `?? ""` on a keyed state would
  // do the moment the query revalidated.
  const [description, setDescription] = useState<string | null>(null);
  const [channels, setChannels] = useState<string | null>(null);
  const descriptionValue = description ?? mine?.description ?? "";
  const channelsValue = channels ?? mine?.channels ?? "";

  /* ROUND 13 H -- what is left to pick. A service already named below
     is not a choice, and offering it is how the list inside the picker
     and the list under it came to name the same thing. */
  const services = useQuery({
    queryKey: ["catalogue", "service"],
    queryFn: () => api.get<CatalogueOption[]>("/v1/catalogue/service?limit=500"),
    enabled: owned,
    staleTime: 300_000,
  });

  const linkedIds = new Set(supporting.map((sv) => sv.id));
  // Round 17 H32 -- the service this one was promoted from is the
  // service itself, not something it rests on (promotion names the
  // business service "ibs.<service code>"); it is not offered.
  const isItself = (sv: CatalogueOption) =>
    (mine?.code != null && sv.code != null && mine.code === `ibs.${sv.code}`) ||
    (mine?.name != null && sv.name === mine.name);
  const pickable = (services.data ?? []).filter(
    (sv) => !linkedIds.has(sv.id) && !isItself(sv),
  );

  /* Round 12 F3, a regression. Round 11 measured the six-layer panel
     move from "1 of 6" to "4 of 6" the moment this owner named two
     dependencies, and called it the most persuasive thing on the page.
     This round it read "1 of 6 layers accounted for" before, during and
     after both additions AND after a reload, for the whole session,
     with the footer stamped twenty-three minutes before he added
     anything. "5 of 6" appeared on his NEXT session.
     
     The dependency writes invalidated `my-estate` and nothing else. The
     panel reads `twin/ibs-layers`, which is cached for a minute and was
     never told, so it served the answer from before his work until the
     cache aged out -- which on a page he stays on, it does not.
     
     Naming what a write actually changes, rather than the one key the
     component that fired it happens to read. */
  const invalidateWhatItRestsOn = () => {
    void qc.invalidateQueries({ queryKey: ["twin", "ibs-layers", ibsId] });
    void qc.invalidateQueries({ queryKey: ["twin", "ibs-story", ibsId] });
  };

  const describe = useMutation<unknown, ApiError, void>({
    mutationFn: () =>
      api.request("PATCH", `/v1/me/estate/ibses/${ibsId}`, {
        ...(descriptionValue.trim() ? { description: descriptionValue.trim() } : {}),
        ...(!internal && channelsValue.trim()
          ? { channels: channelsValue.trim() }
          : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["my-estate"] });
      // The story hub above reads the same IBS.
      void qc.invalidateQueries({ queryKey: ["twin", "ibs-story", ibsId] });
    },
  });

  // D5 -- this cleared the picker and did nothing else: no
  // confirmation, no refresh, and the saved links were never rendered.
  // The owner could not tell whether it had worked, what was already
  // recorded, or how to take one back off, so he pressed it again.
  const addDependency = useMutation<unknown, ApiError, void>({
    mutationFn: () =>
      api.post(`/v1/me/estate/ibses/${ibsId}/services`, { service_id: serviceId }),
    onSuccess: () => {
      const name =
        (services.data ?? []).find((s) => s.id === serviceId)?.name ??
        t("ownerPanel.depends.theService");
      toast.push({
        tone: "success",
        message: t("ownerPanel.depends.added", { name }),
      });
      setServiceId("");
      void qc.invalidateQueries({ queryKey: ["my-estate"] });
      invalidateWhatItRestsOn();
    },
    onError: (err) => toast.push({ tone: "error", message: err.detail }),
  });

  const removeDependency = useMutation<unknown, ApiError, string>({
    mutationFn: (serviceIdToRemove) =>
      api.request(
        "DELETE",
        `/v1/me/estate/ibses/${ibsId}/services/${serviceIdToRemove}`,
      ),
    onSuccess: () => {
      toast.push({ tone: "success", message: t("ownerPanel.depends.removed") });
      void qc.invalidateQueries({ queryKey: ["my-estate"] });
      invalidateWhatItRestsOn();
    },
    onError: (err) => toast.push({ tone: "error", message: err.detail }),
  });

  if (!ibsId || !owned) return null;

  return (
    <Card
      // D4 -- the TOLERANCE chip's landing place. It linked into the
      // admin register, which refuses the owner: the one seat the panel
      // exists for met "Admin role required" above a panel that works.
      id="owner-ibs-panel"
      testid="owner-ibs-panel"
      micro={t(internal ? "ownerPanel.headingInternal" : "ownerPanel.heading")}
      icon="ibs"
      className="space-y-4"
    >
      <p className="mt-1 text-sm text-slate-700">
        {t(internal ? "ownerPanel.introInternal" : "ownerPanel.intro")}
      </p>

      {/* Describe */}
      {/* Round 10 F2 -- the labels were on screen and attached to
          nothing: sibling <label> elements with no htmlFor, so every
          control on the owner's panel measured no accessible name.
          Associating the label that is already there beats adding an
          invisible aria-label, because then the visible words and the
          announced ones cannot drift. */}
      <div className="space-y-1.5">
        <label
          htmlFor={`${fieldId}-description`}
          className="block text-xs font-medium text-slate-700"
        >
          {t(
            internal
              ? "ownerPanel.describe.labelInternal"
              : "ownerPanel.describe.label",
          )}
        </label>
        <textarea
          id={`${fieldId}-description`}
          dir="auto"
          data-testid="owner-panel-description"
          value={descriptionValue}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder={t("ownerPanel.describe.placeholder")}
          className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
        />
        {internal ? (
          <p className="text-xs text-slate-500" data-testid="owner-panel-no-channel">
            {t("ownerPanel.internalNoChannel")}
          </p>
        ) : (
          <>
            <label
              htmlFor={`${fieldId}-channels`}
              className="block text-xs font-medium text-slate-700"
            >
              {t("ownerPanel.serves.label")}
            </label>
            <input
              id={`${fieldId}-channels`}
              dir="auto"
              data-testid="owner-panel-channels"
              value={channelsValue}
              onChange={(e) => setChannels(e.target.value)}
              placeholder={t("ownerPanel.serves.placeholder")}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </>
        )}
        <button
          type="button"
          data-testid="owner-panel-save"
          disabled={
            describe.isPending ||
            (!descriptionValue.trim() && (internal || !channelsValue.trim()))
          }
          onClick={() => describe.mutate()}
          className="rounded bg-brand-brass px-3 py-1 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
        >
          {describe.isPending ? t("ownerPanel.saving") : t("ownerPanel.save")}
        </button>
        {/* Round 16 H27 -- "SaveSaved." in the text. */}
        {describe.isSuccess && " "}
        {describe.isSuccess && (
          <span
            data-testid="owner-panel-saved"
            className="ms-2 text-xs text-emerald-700"
          >
            {t("ownerPanel.saved")}
          </span>
        )}
      </div>

      {/* Wire */}
      <div className="space-y-1.5 border-t border-slate-100 pt-3">
        <label
          htmlFor={`${fieldId}-dependency`}
          className="block text-xs font-medium text-slate-700"
        >
          {t("ownerPanel.depends.label")}
        </label>
        {/* Round 16 H29 -- a select is as wide as its longest option
            unless told otherwise, and with codes on the options it took
            the page to 566 pixels on a 390-pixel phone. It may shrink
            now, and Add wraps beneath it where there is no room. */}
        <div className="flex flex-wrap gap-2">
          <select
            id={`${fieldId}-dependency`}
            data-testid="owner-panel-service"
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
            className="w-full min-w-0 flex-1 basis-48 rounded border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">{t("ownerPanel.depends.pick")}</option>
            {/* ROUND 13 H, ninth round -- "a service listed as a
                dependency of itself".
                
                Two causes, neither of them bad data. The picker
                offered services ALREADY linked, so the list below it
                and the list inside it named the same thing; and it
                printed the bare name, so on an estate where
                "SAMA regulatory reporting" rests on "Regulatory
                Reporting" the option read as the page's own subject.
                
                Already-linked services are gone, and what is left
                carries its code -- the one thing that is unambiguously
                the service and not the business service above it. */}
            {pickable.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code
                  ? t("ownerPanel.depends.optionWithCode", {
                      name: s.name,
                      code: s.code,
                    })
                  : s.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            data-testid="owner-panel-add-dependency"
            disabled={!serviceId || addDependency.isPending}
            onClick={() => addDependency.mutate()}
            className="min-h-[44px] shrink-0 rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1 text-sm text-brand-brassDark disabled:opacity-50"
          >
            {t("ownerPanel.depends.add")}
          </button>
        </div>
        {addDependency.isError && (
          <span
            data-testid="owner-panel-dependency-error"
            className="text-xs text-rose-700"
          >
            {addDependency.error.detail}
          </span>
        )}
        {/* What is recorded now. An owner asked to confirm his
            dependencies has to be able to see them. */}
        {supporting.length === 0 ? (
          <p
            data-testid="owner-panel-no-dependencies"
            className="text-xs text-slate-500"
          >
            {t("ownerPanel.depends.none")}
          </p>
        ) : (
          <ul data-testid="owner-panel-dependencies" className="space-y-1">
            {supporting.map((sv) => (
              <li
                key={sv.id}
                data-testid={`owner-panel-dependency-${sv.id}`}
                className="flex items-center gap-2 text-xs text-slate-700"
              >
                <span className="flex-1 truncate">{sv.name}</span>
                {/* Who said so. An owner may take back what he
                    asserted; a CMDB-derived edge is the estate's own
                    finding and worth pausing over. */}
                <span className="text-[10px] text-slate-400">
                  {sv.source === "owner_declared"
                    ? t("ownerPanel.depends.byYou")
                    : t("ownerPanel.depends.fromCmdb")}
                </span>
                <button
                  type="button"
                  data-testid={`owner-panel-remove-dependency-${sv.id}`}
                  disabled={removeDependency.isPending}
                  onClick={() => removeDependency.mutate(sv.id)}
                  className="text-slate-400 hover:text-rose-700 disabled:opacity-50"
                >
                  {t("ownerPanel.depends.remove")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* A3 -- the same form the Resilience Lead reaches, rather than
          a second copy of it. */}
      <div className="border-t border-slate-100 pt-3">
        <ToleranceDeclarationForm ibsId={ibsId} testIdPrefix="owner-panel-declare" />
      </div>
    </Card>
  );
}
